import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { FaInfoCircle } from "react-icons/fa";
import SearchBox from "../../../../Components/SearchBox";
import { Formik } from "formik";
import { useQuery } from "@tanstack/react-query";
import { SymptomSearchBarInitialValues } from "../../constant";
import {
  Confirmation,
  ErrorToast,
  SuccessToast,
} from "../../../../Utils/SweetAlert";
import {
  useMutateDeleteSymptom,
  useMutateDragDropSymptom,
} from "../../hooks/useApiHook";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { GetSymptomList, updateSymptomStatus } from "../../../../Api/Symptom";
import { EditButton, DeleteButton, AddButton } from "../../../../Components";
import Tooltip from "@mui/material/Tooltip";

const SymptomList = () => {
  const formRef = useRef();
  const deleteSymptom = useMutateDeleteSymptom();
  const dragAndDropSymptom = useMutateDragDropSymptom();
  const [symptomList, setSymptomList] = useState(null);

  const {
    isFetching: isSymptomLoading,
    data: initialData,
    refetch,
  } = useQuery({
    queryKey: ["get-symptom-list"],
    queryFn: () => GetSymptomList({
      search: formRef?.current?.values?.search || "",
    }),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server error.");
    },
  });

  useEffect(() => {
    if (initialData) {
      setSymptomList(initialData);
    }
  }, [initialData]);

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const { source, destination } = result;
    const items = Array.from(symptomList.data);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);
    setSymptomList({ ...symptomList, data: items });

    dragAndDropSymptom.mutate(
      {
        symptomId: reorderedItem._id,
        newOrder: destination.index,
      },
      {
        onSuccess: (response) => {
          SuccessToast(response?.message || "Drag and drop successful.");
          refetch();
        },
        onError: (error) => {
          ErrorToast(error?.response?.data?.message || "Failed to reorder.");
        },
      }
    );
  };

  const handleConfirmRequest = (values) => {
    Confirmation(
      values?.isOnboarded === true
        ? "Are you sure you want to active user is Onboarded?"
        : "Are you sure you want to inactive user is Onboarded?"
    ).then(async (deleteConfirm) => {
      if (deleteConfirm.isConfirmed) {
        await updateSymptomStatus({ symptomId: values?.symptomId })
          .then((res) => {
            SuccessToast(res?.data?.message || "status Updated");
            refetch();
          })
          .catch((err) => {
            if (typeof err?.response?.data?.message !== "undefined") {
              ErrorToast(err?.response?.data?.message || "Server Error!!");
            } else {
              ErrorToast(err?.message || "Server Error!!");
            }
          });
      }
    });
  };
  return (
    <>
      <Helmet>
        <title>OVE | Admin Symptom</title>
      </Helmet>
      <div className="symptom-page-wrapper admin-user">
        <div className="content container-fluid">
          <div className="admin-wrapper">
            <div className="page-header">
              <div className="col">
                <h1 className="page-title">Symptom</h1>
              </div>
              <div className="col-12 col-md-3 col-lg-2 px-2">
                <Formik
                  initialValues={initialData}
                  innerRef={formRef}
                >
                  {() => {
                    return (
                      <form>
                        <SearchBox refetch={refetch} />
                      </form>
                    );
                  }}
                </Formik>
              </div>
              <div>
                <AddButton
                  to="/admin/add-symptom"
                  permission="create-symptom"
                />
              </div>
            </div>
            <div className="col-sm-12 table-width">
              <div className="cards-table card-table">
                <div className="card-body">
                  <div className="table-responsive custom-pagination table-no-padding">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <table className="table symptom-table datatable table-manager">
                        <thead className="thead-light">
                          <tr>
                            <th>Name</th>
                            <th>
                              Onboarding status
                              <Tooltip
                                title={`All listed symptoms are displayed on the homepage. This field indicates whether the symptoms should be shown during the onboarding process. If the flag is set to \"Yes\", the symptoms will be displayed in the onboarding flow; otherwise, they will only appear on the homepage.`}
                                placement="top"
                              >
                                <span style={{ marginLeft: 6, verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center' }}>
                                  <FaInfoCircle size={16} />
                                </span>
                              </Tooltip>
                            </th>
                            <th className="col-action text-center">Action</th>
                          </tr>
                        </thead>

                        {isSymptomLoading && (
                          <tbody>
                            <tr>
                              <td align="center" colSpan={3}>
                                <div className="d-flex justify-content-center align-items-center py-4">
                                  <div className="spinner-border loader-theme" role="status" />
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        )}

                        {!isSymptomLoading && (
                          <Droppable droppableId="droppable">
                            {(provided) => (
                              <tbody
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {symptomList?.data.map((item, index) => (
                                  <Draggable
                                    key={item._id}
                                    draggableId={item._id}
                                    index={index}
                                  >
                                    {(provided) => (
                                      <tr
                                        key={index}
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className="table disable-row"
                                      >
                                        <td className="user-padding-remove">
                                          <Tooltip
                                            title={item.name}
                                            placement="top"
                                          >
                                            <span>
                                              {item.name?.length >= 45
                                                ? item.name.substring(0, 40) +
                                                "..."
                                                : item.name || "-"}
                                            </span>
                                          </Tooltip>
                                        </td>
                                        <td>
                                          {item?.isOnboarded == true ? (
                                            <button
                                              onClick={() =>
                                                handleConfirmRequest({
                                                  symptomId: item?._id,
                                                  isOnboarded:
                                                    item?.isOnboarded === true
                                                      ? false
                                                      : true,
                                                })
                                              }
                                              class="symptom-active-btn btn-success"
                                              disabled={
                                                item?.role === "superadmin"
                                                  ? true
                                                  : false
                                              }
                                            >
                                              Yes
                                            </button>
                                          ) : (
                                            (
                                              <button
                                                onClick={() =>
                                                  handleConfirmRequest({
                                                    symptomId: item?._id,
                                                    isOnboarded:
                                                      item?.isOnboarded ===
                                                        true
                                                        ? false
                                                        : true,
                                                  })
                                                }
                                                class="symptom-inactive-btn btn-danger"
                                                disabled={
                                                  item?.role === "superadmin"
                                                    ? true
                                                    : false
                                                }
                                              >
                                                No
                                              </button>
                                            ) || "-"
                                          )}
                                        </td>
                                        <td className="col-action text-center">
                                          <EditButton
                                            to="/admin/edit-symptom"
                                            state={{ id: item?._id }}
                                            permission="edit-symptom"
                                          />
                                          <DeleteButton
                                            onDelete={() => {
                                              Confirmation("Are you sure you want to delete symptom?").then(
                                                async (deleteConfirm) => {
                                                  if (deleteConfirm.isConfirmed) {
                                                    deleteSymptom.mutate(item?._id, {
                                                      onSuccess: (response) => {
                                                        SuccessToast(response?.message || "Delete successful.");
                                                        refetch();
                                                      },
                                                      onError: (error) => {
                                                        ErrorToast(error?.response?.data?.message || "Server error.");
                                                      },
                                                    });
                                                  }
                                                }
                                              );
                                            }}
                                            permission="delete-symptom"
                                          />
                                        </td>
                                      </tr>
                                    )}
                                  </Draggable>
                                ))}
                                {symptomList?.data?.length === 0 && (
                                  <tr>
                                    <td align="left" colSpan={8}>
                                      No Records Found.
                                    </td>
                                  </tr>
                                )}
                                {provided.placeholder}
                              </tbody>
                            )}
                          </Droppable>
                        )}
                      </table>
                    </DragDropContext>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
    </>
  );
};

export default SymptomList;
